import { Mail, MessageCircle, Phone, UserPlus } from "lucide-react";
import type { ComponentType } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { AgenteSlot as AgenteSlotData } from "@/types/api";

/** Ordine e icone dei canali di contatto dell'agente. */
const CANALI: { key: string; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { key: "email", label: "Email", icon: Mail },
  { key: "telefono", label: "Telefono", icon: Phone },
  { key: "chat", label: "Chat", icon: MessageCircle },
];

function Avatar({ nome, url }: { nome: string | null; url: string | null }) {
  if (url) {
    return (
      <img
        src={url}
        alt={nome ?? "Agente"}
        className="size-10 rounded-full object-cover"
      />
    );
  }
  const iniziale = (nome ?? "?").trim().charAt(0).toUpperCase();
  return (
    <div
      className="flex size-10 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-semibold text-white"
      aria-hidden="true"
    >
      {iniziale}
    </div>
  );
}

/**
 * Slot agente di una `ModuloCard` (SPEC-A §5.2). Se `agente` è null o non attivo,
 * mostra «agente in arrivo» (nessun badge AI, nessun canale).
 */
export function AgenteSlot({ agente }: { agente: AgenteSlotData | null }) {
  if (!agente || !agente.attivo) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-dashed border-brand-200/70 bg-brand-50/30 p-3">
        <div
          className="flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground"
          aria-hidden="true"
        >
          <UserPlus className="size-5" />
        </div>
        <div className="text-xs">
          <p className="font-medium text-foreground">Agente in arrivo</p>
          <p className="text-muted-foreground">
            Questo modulo avrà presto il suo assistente AI.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-brand-200 bg-brand-50/60 p-3">
      <div className="flex items-start gap-3">
        <Avatar nome={agente.nome} url={agente.avatar_url} />
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-foreground">
              {agente.nome}
            </span>
            {agente.badge_ai && (
              <span className="rounded bg-brand-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                AI
              </span>
            )}
          </div>
          {agente.riferimento_umano && (
            <p className="text-xs text-muted-foreground">
              Riferimento umano: {agente.riferimento_umano}
            </p>
          )}
          <CanaliRow canali={agente.canali} />
        </div>
      </div>
    </div>
  );
}

function CanaliRow({ canali }: { canali: Record<string, boolean> }) {
  return (
    <TooltipProvider>
      <div className="flex items-center gap-2 pt-1">
        {CANALI.map(({ key, label, icon: Icon }) => {
          const attivo = !!canali[key];
          return (
            <Tooltip key={key}>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    "inline-flex size-6 items-center justify-center rounded",
                    attivo ? "text-brand-600" : "text-slate-300"
                  )}
                  aria-label={`${label}${attivo ? "" : " — presto"}`}
                >
                  <Icon className="size-4" />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {label}
                {attivo ? "" : " · presto"}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
